import { MapContainer, TileLayer, Marker } from 'react-leaflet'
import { useMemo, useRef, useEffect } from 'react'
import { toast } from '../services/toastService'
import { createLogger } from '../utils/logger'
import { getZoomLevel } from '../utils/mapUtils'
import { ComponentSelectorButton } from '../utils/debugWrapper'
import { initializeLeafletIcons } from './map/utils'
import {
  getMapContainerStyle,
  getMapWrapperClasses,
  getMapInnerClasses,
  getSearchControlsClasses,
  getMapContentClasses,
  generateMarkerKey,
  generateMapContainerKey,
  shouldShowSearchControls,
  getCommonMapContainerProps,
  getTileLayerUrl
} from '../utils/mapComponentUtils'
import { EntityModeMarker } from './map/MarkerComponents'
import ImmersiveMapPanelContent from '../components/map/ImmersiveMapPanelContent'
import MobileMapControls from '../components/map/MobileMapControls'
import { MapZoomTracker } from '../components/map/MapZoomTracker'
import { MyLocationControl } from '../components/map/MyLocationControl'
import DraggablePopupHandler from '../components/DraggablePopupHandler'
import UserLocationMarkerWithRadius from '../components/map/UserLocationMarkerWithRadius'
import MapControllers from '../components/map/MapControllers'
import L from 'leaflet'
import EntityGallery from '../components/EntityGallery'
import { useEntityDetailMapState } from '../hooks/map/useEntityDetailMapState'
import { useEntityNotifyOnSpot } from '../hooks/map/useEntityNotifyOnSpot'
import { useConfirmLocationClick } from '../hooks/map/useConfirmLocationClick'
import { useMapPanelResize } from '../hooks/map/useMapPanelResize'
import { useResponsiveLayout } from '../hooks/useResponsiveLayout'
import SpotModeBanner from '../components/map/SpotModeBanner'
import SpotModePanel from '../components/map/SpotModePanel'
import SearchLocationInput from '../components/map/SearchLocationInput'
import EntitySearchInput from '../components/map/EntitySearchInput'
import { getCategoryIcon, getCategoryColor, getCategoryIconColor } from '../utils/entityImageUtils'

initializeLeafletIcons()

/**
 * FocalEntityPulseMarker - Animated ring highlighting the primary entity location.
 * Uses a CSS keyframe animation (entity-focal-pulse in index.css).
 * Non-interactive, renders below all other markers via zIndexOffset.
 */
function FocalEntityPulseMarker({ lat, lng }) {
  const focalIcon = useMemo(() => L.divIcon({
    className: '',
    html: '<div class="entity-focal-pulse"></div>',
    iconSize: [60, 60],
    iconAnchor: [30, 30],
  }), [])
  if (!lat || !lng) return null
  return <Marker position={[lat, lng]} icon={focalIcon} interactive={false} zIndexOffset={-200} />
}

/**
 * EntityDetailMap - Refactored to use useEntityDetailMapState + map-layer hooks
 * (notify-on-spot, confirm-location, and panel-resize logic extracted to hooks/map/)
 */
export default function EntityDetailMap({ 
  entityId = null, 
  entityName = null, 
  entityLocation = null, 
  entityDetails = null, 
  canEditEntity = false, 
  isFullscreenMode = false, 
  isImmersiveMode = false, 
  immersiveTabs = null, 
  activeTab = 'search', 
  onTabChange = null, 
  panelState = 'expanded', 
  onPanelStateChange = null, 
  componentSource = 'Unknown', 
  initialChannelId: initialChannelIdProp = null, 
  onChannelSelected = null, 
  onUnreadCountChange = null,
  claimPrefill = null,
  onClaimPrefillConsumed = null 
}) {
  const state = useEntityDetailMapState({
    entityId,
    isImmersiveMode,
    onTabChange,
    panelState,
    activeTab,
    initialChannelIdProp,
    entityDetails
  })

  const { isDesktop } = useResponsiveLayout()

  // Show confirm prompt when user arrives via push notification deeplink (?confirm=sightingId)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const confirmId = params.get('confirm')
    if (confirmId) {
      toast.success(
        state.t?.('confirm.notificationPrompt', "Tap 'Confirm Location' to confirm this sighting and earn trust points!"),
        { duration: 6000 }
      )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Store marker refs to programmatically trigger them
  const markerRefs = useRef({})

  const {
    t,
    navigate,
    user,
    logout,
    isEntityMode,
    entities,
    setEntities,
    userLocation,
    setUserLocation,
    searchLocation,
    setSearchLocation,
    searchLocationName,
    setSearchLocationName,
    isLoading,
    mapRef,
    currentOpenPopupRef,
    entityGallery,
    galleryRenderKey,
    isFavorite,
    toggleFavorite,
    isTogglingFavorite,
    favorites,
    updateFavoriteNotify,
    locationSource,
    isGeocoding,
    entitySearchQuery,
    setEntitySearchQuery,
    setFocusedEntityId,
    setIsolatedEntityId,
    isSpotPanelMinimized,
    setIsSpotPanelMinimized,
    isSearchPanelMinimized,
    setIsSearchPanelMinimized,
    initialChannelId,
    entitySearchResults,
    isSearching,
    showSearchDropdown,
    setShowSearchDropdown,
    isSpotModeActive,
    spotModeLocation,
    setSpotModeLocation,
    spotModeEntity,
    setSpotModeEntity,
    spotModeEntityType,
    setSpotModeEntityType,
    spotModeNotes,
    setSpotModeNotes,
    spotModeEntityStatus,
    setSpotModeEntityStatus,
    spotModeSocials,
    setSpotModeSocials,
    spotModeImages,
    setSpotModeImages,
    isSubmittingSpot,
    spotModeStartTime,
    deactivateSpotMode,
    loginFormData,
    loginErrors,
    handleLoginChange,
    handleLoginSubmit,
    registerFormData,
    registerErrors,
    handleRegisterChange,
    handleRegisterSubmit,
    isAuthLoading,
    googleAuth,
    stats,
    statsLoading,
    radius,
    mapCenter,
    filteredEntities,
    activeFilters,
    handleFilterToggle,
    openNowActive,
    toggleOpenNow,
    handleRadiusChange,
    geocodeAddress,
    handleSelectEntity,
    handleCurrentLocation,
    handleSpotModeActivation,
    handleSpotSubmit,
    handleEntityConfirmSuccess,
    showUserLocation
  } = state

  // Get latest sighting for isOwnSighting check
  const latestSighting = entities && entities.length > 0 ? entities[0] : null

  const { entityNotifyOnSpot, handleEntityNotifyToggle } = useEntityNotifyOnSpot({
    entityId, favorites, updateFavoriteNotify, t
  })

  const handleConfirmLocationClick = useConfirmLocationClick({
    entities, currentOpenPopupRef, mapRef, markerRefs, onPanelStateChange, t
  })

  useMapPanelResize({ isImmersiveMode, panelState, mapRef })

  return (
    <div className={getMapWrapperClasses(isFullscreenMode, isImmersiveMode)}>
      <div className={getMapInnerClasses(isFullscreenMode, isImmersiveMode)}>
        {/* Unified Search Controls */}
        {shouldShowSearchControls(isSpotModeActive, isFullscreenMode, isImmersiveMode, isEntityMode) && (
          <div className={getSearchControlsClasses(isImmersiveMode)}>
            {isImmersiveMode ? (
              <>
              <ImmersiveMapPanelContent
                panelState={panelState}
                isSearchPanelMinimized={isSearchPanelMinimized}
                activeTab={activeTab}
                onTabChange={onTabChange}
                onPanelStateChange={onPanelStateChange}
                immersiveTabs={immersiveTabs}
                user={user}
                onLogout={logout}
                searchLocation={searchLocation}
                onSearchLocationChange={(value) => {
                  setSearchLocation(value)
                  if (!value.trim()) {
                    setUserLocation(null)
                    setSearchLocationName('')
                    setEntities([])
                  }
                }}
                radius={radius}
                onRadiusChange={handleRadiusChange}
                onGeocode={geocodeAddress}
                onCurrentLocation={state.getUserLocation}
                onSpotClick={handleSpotModeActivation}
                isGeocoding={isGeocoding}
                locationSource={locationSource}
                entitySearchQuery={entitySearchQuery}
                onEntitySearchChange={setEntitySearchQuery}
                entitySearchInputRef={state.entitySearchInputRef}
                isSearching={isSearching}
                searchResults={entitySearchResults}
                showDropdown={showSearchDropdown}
                onSelectEntity={handleSelectEntity}
                onCloseDropdown={() => setShowSearchDropdown(false)}
                userLocation={userLocation}
                searchLocationName={searchLocationName}
                entities={entities}
                isLoading={isLoading}
                stats={stats}
                statsLoading={statsLoading}
                pendingConfirmations={state.pendingConfirmations}
                loadingConfirmations={state.loadingConfirmations}
                confirmingId={state.confirmingId}
                loginFormData={loginFormData}
                loginErrors={loginErrors}
                handleLoginChange={handleLoginChange}
                handleLoginSubmit={handleLoginSubmit}
                registerFormData={registerFormData}
                registerErrors={registerErrors}
                handleRegisterChange={handleRegisterChange}
                handleRegisterSubmit={handleRegisterSubmit}
                isAuthLoading={isAuthLoading}
                googleLoginUrl={googleAuth}
                activeFilters={activeFilters}
                onFilterToggle={handleFilterToggle}
                openNowActive={openNowActive}
                onToggleOpenNow={toggleOpenNow}
                entityCount={entities.length}
                isEntityMode={isEntityMode}
                entityDetails={entityDetails}
                latestSighting={latestSighting}
                entityGallery={entityGallery}
                galleryRenderKey={galleryRenderKey}
                entityId={entityId}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                isTogglingFavorite={isTogglingFavorite}
                entityNotifyOnSpot={entityNotifyOnSpot}
                onEntityNotifyToggle={handleEntityNotifyToggle}
                getCategoryIcon={getCategoryIcon}
                getCategoryColor={getCategoryColor}
                getCategoryIconColor={getCategoryIconColor}
                canEditEntity={canEditEntity}
                onNavigateToEdit={() => navigate(`/admin/entity/${entityId}/edit`)}
                onConfirmLocationClick={handleConfirmLocationClick}
                initialChannelId={initialChannelId}
                onChannelSelected={onChannelSelected}
                onUnreadCountChange={onUnreadCountChange}
                claimPrefill={claimPrefill}
                onClaimPrefillConsumed={onClaimPrefillConsumed}
                entityName={entityDetails?.name || null}
                entityCategory={entityDetails?.category || null}
              />
              {/* Mobile map controls moved to fixed top-right — see below */}
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <SearchLocationInput
                    searchLocation={searchLocation}
                    onSearchLocationChange={(value) => {
                      setSearchLocation(value)
                      if (!value.trim()) {
                        setUserLocation(null)
                        setSearchLocationName('')
                        setEntities([])
                      }
                    }}
                    radius={radius}
                    onRadiusChange={handleRadiusChange}
                    onGeocode={geocodeAddress}
                    onCurrentLocation={state.getUserLocation}
                    isGeocoding={isGeocoding}
                    locationSource={locationSource}
                    className="sm:w-auto"
                  />
                  <div className="flex-1">
                    <EntitySearchInput
                      value={entitySearchQuery}
                      onChange={setEntitySearchQuery}
                      onFocus={() => entitySearchResults.length > 0 && setShowSearchDropdown(true)}
                      inputRef={state.entitySearchInputRefAlt}
                      isSearching={isSearching}
                      searchResults={entitySearchResults}
                      showDropdown={showSearchDropdown}
                      onSelectEntity={handleSelectEntity}
                      onCloseDropdown={() => setShowSearchDropdown(false)}
                      showNoResults={showSearchDropdown && !isSearching && entitySearchQuery.length > 1 && entitySearchResults.length === 0}
                      userLocation={userLocation}
                      size="default"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Map controls — fixed right-side vertical stack in immersive mode (all screen sizes) */}
        {isImmersiveMode && (
          <MobileMapControls
            mapRef={mapRef}
            className={`fixed right-4 z-[800] flex-col gap-2 ${isDesktop ? 'top-1/2 -translate-y-1/2' : 'bottom-[180px]'}`}
            onLocationClick={handleCurrentLocation}
            onSpotClick={handleSpotModeActivation}
          />
        )}

        {/* Entity Mode Map */}
        {isEntityMode && (
          <div className={isImmersiveMode ? "relative h-full" : "bg-white rounded-lg shadow p-2 sm:p-6 mb-3 sm:mb-6"}>
            {!isImmersiveMode && !isSpotModeActive && (
              <>
                {isLoading && (
                  <div className="bg-white-50 border border-primary-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                    <div className="flex items-center gap-3">
                      <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      <p className="text-primary-800 text-sm font-medium">{t('map.loadingLocations')}</p>
                    </div>
                  </div>
                )}
                {!isLoading && userLocation && entities.length === 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                    <p className="text-gray-700 text-sm">{t('map.noLocationsFound')}</p>
                  </div>
                )}
                <div className="mb-4">
                  <EntityGallery
                    key={galleryRenderKey}
                    entityId={entityId}
                    images={entityGallery}
                  />
                </div>
              </>
            )}

            <MapContainer
              key={generateMapContainerKey(`entity-${entityId}`, isFullscreenMode, isImmersiveMode)}
              center={mapCenter}
              zoom={getZoomLevel(radius)}
              {...getCommonMapContainerProps()}
              style={getMapContainerStyle(isFullscreenMode, isImmersiveMode)}
            >
              <TileLayer
                key="tile-layer"
                url={getTileLayerUrl()}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <MapZoomTracker />
              <MyLocationControl 
                onLocationClick={handleCurrentLocation}
                onSpotClick={handleSpotModeActivation}
              />
              <DraggablePopupHandler 
                isImmersiveMode={isImmersiveMode}
                isSearchPanelMinimized={isSearchPanelMinimized}
                setIsSearchPanelMinimized={setIsSearchPanelMinimized}
                onTabChange={onTabChange}
                activeTab={activeTab}
                panelState={panelState}
              />
              <UserLocationMarkerWithRadius
                userLocation={userLocation}
                radius={radius}
                entities={entities}
                showUserLocation={showUserLocation}
                isEntityMode={isEntityMode}
                isImmersiveMode={isImmersiveMode}
              />
              {/* Focal pulse ring on the primary entity location */}
              {entityLocation?.latitude && entityLocation?.longitude && (
                <FocalEntityPulseMarker
                  lat={entityLocation.latitude}
                  lng={entityLocation.longitude}
                />
              )}
              {filteredEntities.map((entity) => {
                const uniqueKey = generateMarkerKey(entity.id, entity.currentLatitude, entity.currentLongitude, 'entity')
                return (
                  <EntityModeMarker
                    key={uniqueKey}
                    entity={entity}
                    user={user}
                    navigate={navigate}
                    onConfirmSuccess={handleEntityConfirmSuccess}
                    isImmersiveMode={isImmersiveMode}
                    onTabChange={onTabChange}
                    t={t}
                    isSearchPanelMinimized={isSearchPanelMinimized}
                    setIsSearchPanelMinimized={setIsSearchPanelMinimized}
                    currentOpenPopupRef={currentOpenPopupRef}
                    panelState={panelState}
                    onPanelStateChange={onPanelStateChange}
                    markerRefs={markerRefs}
                  />
                )
              })}
              <MapControllers
                mapRef={mapRef}
                isImmersiveMode={isImmersiveMode}
                onTabChange={onTabChange}
                isSearchPanelMinimized={isSearchPanelMinimized}
                setIsSearchPanelMinimized={setIsSearchPanelMinimized}
                isSpotModeActive={isSpotModeActive}
                onLocationSelect={setSpotModeLocation}
                setFocusedEntityId={setFocusedEntityId}
                setIsolatedEntityId={setIsolatedEntityId}
                userLocation={userLocation}
                spotModeLocation={spotModeLocation}
                setSpotModeLocationSetTime={null}
                markers={entities}
                isEntityMode={isEntityMode}
                radius={radius}
                panelState={panelState}
              />
            </MapContainer>
          </div>
        )}

        {/* Spot Mode UI */}
        {isSpotModeActive && (
          <>
            <SpotModeBanner
              t={t}
              spotModeLocation={spotModeLocation}
              spotModeEntity={spotModeEntity}
              spotModeNotes={spotModeNotes}
              spotModeImages={spotModeImages}
              spotModeStartTime={spotModeStartTime}
              onCancel={() => deactivateSpotMode()}
            />
            {spotModeLocation && (
              <SpotModePanel
                t={t}
                location={spotModeLocation}
                entity={spotModeEntity}
                onEntityChange={setSpotModeEntity}
                entityType={spotModeEntityType}
                onEntityTypeChange={setSpotModeEntityType}
                entities={entities}
                notes={spotModeNotes}
                onNotesChange={setSpotModeNotes}
                entityStatus={spotModeEntityStatus}
                onEntityStatusChange={setSpotModeEntityStatus}
                socials={spotModeSocials}
                onSocialsChange={setSpotModeSocials}
                images={spotModeImages}
                onImagesChange={setSpotModeImages}
                onSubmit={handleSpotSubmit}
                onCancel={() => deactivateSpotMode()}
                isSubmitting={isSubmittingSpot}
                isMinimized={isSpotPanelMinimized}
                onMinimizeToggle={setIsSpotPanelMinimized}
                user={user}
                isAuthenticated={state.isAuthenticated}
              />
            )}
          </>
        )}
      </div>
      
      {/* Component Selector - Only visible when admin has enabled debug mode */}
      <ComponentSelectorButton floating={true} />
    </div>
  )
}
